import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMouseScroll } from '@/hooks/useMouseScroll';
import { Box, Flex, Image, mergeRefs, type ImageProps } from '@/ui';
import { ZoomSlider } from '../ZoomSlider';
import type { ImageRendererProps } from './types';

export const ImageRenderer = ({
  src,
  maxImageWidth,
  maxImageHeight,
  zoomEnabled,
  ...imageProps
}: ImageRendererProps & ImageProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const { ref } = useMouseScroll({ enabled: zoomEnabled });
  const [previewHeight, setPreviewHeight] = useState(0);
  const [previewWidth, setPreviewWidth] = useState(0);

  // Reset container height when scale changes
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.height = 'auto';
    }
  }, [scale]);

  useEffect(() => setScale(1), [src]);

  useLayoutEffect(() => {
    const container = previewRef.current;
    if (container) {
      const update = () => {
        const { height, width } = container.getBoundingClientRect();
        setPreviewHeight(height);
        setPreviewWidth(width);
      };

      update();

      const observer = new ResizeObserver(update);
      observer.observe(container);
      window.addEventListener('resize', update);

      return () => {
        observer.disconnect();
        window.removeEventListener('resize', update);
      };
    }
  }, []);

  return (
    <Box pos="relative" ref={containerRef} w="100%">
      {zoomEnabled && (
        <Flex justify="center" mb="4">
          <ZoomSlider scale={scale} onScaleChange={setScale} />
        </Flex>
      )}

      <Box
        {...(!zoomEnabled
          ? { display: 'flex', flexDirection: 'column', alignItems: 'center' }
          : {})}
        height={previewHeight > 0 ? previewHeight : 'auto'}
        overflow={scale > 1 ? 'auto' : 'hidden'}
        ref={mergeRefs(ref, previewRef)}
        userSelect="none"
      >
        <Box
          h={previewHeight === 0 ? 'auto' : `${previewHeight * scale}px`}
          maxH={maxImageHeight}
          maxW={maxImageWidth}
          transition="width 0.2s ease, height 0.2s ease"
          w={previewWidth === 0 ? 'auto' : `${previewWidth * scale}px`}
          willChange="width, height"
        >
          <Image
            cursor={zoomEnabled ? 'grab' : undefined}
            draggable={false}
            h="100%"
            objectFit="contain"
            src={src}
            w="100%"
            {...imageProps}
          />
        </Box>
      </Box>
    </Box>
  );
};
